import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { ArrowLeft, Search, ChevronRight } from 'lucide-react';

const FazerFezinhaPage = () => {
  const navigate = useNavigate();
  const [selectedStrategy, setSelectedStrategy] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const stepGuide = [
    {
      title: 'Comece com Apostas Simples',
      phase: 'INICIANTE',
      bankroll: 'R$10-50',
      items: [
        'Aposte apenas 5% da banca por vez',
        'Escolha odds entre 1.50 e 2.00',
        'Foque em mercados seguros',
        'Meta: Dobrar a banca em 30 dias'
      ],
      expectedReturn: 100
    },
    {
      title: 'Evolua para Duplas e Triplas',
      phase: 'INTERMEDIÁRIO',
      bankroll: 'R$50-200',
      items: [
        'Com R$20-50, comece multiplas de 2-3 jogos',
        'Odds totais entre 3.00 e 5.00',
        'Nunca aposte mais de 10% da banca',
        'Use Cash Out quando disponível'
      ],
      expectedReturn: 300
    },
    {
      title: 'Multiplas Seguras',
      phase: 'AVANÇADO',
      bankroll: 'R$200-500',
      items: [
        'Combine favoritos com odds 1.30-1.50',
        '3-4 jogos por bilhete',
        'Aposte R$5-10 quando tiver R$100+',
        '2-3 multiplas certas por semana'
      ],
      expectedReturn: 500
    },
    {
      title: 'Gestão Profissional',
      phase: 'EXPERT',
      bankroll: 'R$500+',
      items: [
        'Separe lucro do capital inicial',
        'Retire 50% dos lucros mensalmente',
        'Mantenha planilha de controle',
        'Nunca persiga prejuízos'
      ],
      expectedReturn: 1000
    }
  ];

  const multiplaTypes = [
    {
      name: 'Multipla Ouro',
      type: 'MAIS SEGURA',
      description: '3 favoritos com odd 1.40-1.50',
      totalOdds: 3.50,
      successRate: 68,
      investment: 5,
      category: 'conservadora',
      bestFor: 'Iniciantes'
    },
    {
      name: 'Multipla Diamante',
      type: 'EQUILIBRADA',
      description: '2 favoritos + 1 zebra controlada',
      totalOdds: 6.00,
      successRate: 42,
      investment: 3,
      category: 'moderada',
      bestFor: 'Intermediários'
    },
    {
      name: 'Multipla Foguete',
      type: 'ALTO RISCO',
      description: '4-5 jogos com odds 1.80-2.20',
      totalOdds: 15.00,
      successRate: 18,
      investment: 1,
      category: 'agressiva',
      bestFor: 'Experientes'
    },
    {
      name: 'Multipla Bronze',
      type: 'ULTRA SEGURA',
      description: '2 favoritos com odd 1.20-1.30',
      totalOdds: 1.80,
      successRate: 82,
      investment: 10,
      category: 'conservadora',
      bestFor: 'Conservadores'
    },
    {
      name: 'Multipla Prata',
      type: 'BALANCEADA',
      description: '3 jogos com odd 1.60-1.80',
      totalOdds: 8.50,
      successRate: 35,
      investment: 2,
      category: 'moderada',
      bestFor: 'Estratégicos'
    },
    {
      name: 'Multipla Relâmpago',
      type: 'ESPECULATIVA',
      description: '6+ jogos com odds variadas',
      totalOdds: 50.00,
      successRate: 8,
      investment: 0.5,
      category: 'agressiva',
      bestFor: 'Apostadores de risco'
    }
  ];

  const tips = [
    'Nunca aposte dinheiro que você não pode perder',
    'Mantenha registro de todas as apostas',
    'Melhores odds aparecem 2-3 horas antes do jogo',
    'Saque 50% dos lucros todo mês',
    'Sexta e sábado têm mais oportunidades',
    'Use conta separada para apostas',
    'Trate como diversão, não investimento',
    'Nunca faça Martingale',
    'Aceite derrotas como parte do jogo',
    'Foque em ligas que você conhece'
  ];

  const faqs = [
    {
      question: 'Quanto preciso para começar?',
      answer: 'Você pode começar com apenas R$10. Nossa estratégia é desenhada para crescimento gradual, começando com apostas de R$0.50 (5% de R$10) e aumentando conforme sua banca cresce.'
    },
    {
      question: 'É possível viver de apostas?',
      answer: 'Apostas devem ser tratadas como entretenimento, não como fonte de renda principal. Nosso foco é diversão responsável com possibilidade de lucros consistentes.'
    },
    {
      question: 'Qual a taxa de acerto da IA?',
      answer: 'Nossa IA tem precisão profissional em apostas simples e 68% em multiplas seguras (Ouro). Mas lembre-se: no mundo das apostas, gestão de banca é mais importante que taxa de acerto.'
    },
    {
      question: 'Como funciona a estratégia R$10 para R$1000?',
      answer: 'É um processo gradual de 3-6 meses, dobrando a banca mensalmente através de apostas disciplinadas e gestão rigorosa. Não é um esquema rápido, mas uma estratégia sustentável.'
    }
  ];

  const STRATEGY_LABELS = {
    all: 'Todas',
    conservadora: 'Conservadora',
    moderada: 'Moderada',
    agressiva: 'Agressiva'
  };

  const filteredMultiplas = multiplaTypes.filter(multipla => {
    const matchesCategory = selectedStrategy === 'all' || multipla.category === selectedStrategy;
    const matchesSearch = multipla.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          multipla.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const strategyCount = multiplaTypes.reduce((acc, multipla) => {
    acc[multipla.category] = (acc[multipla.category] || 0) + 1;
    return acc;
  }, {});

  // Structured Data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Como Fazer a Fezinha: Guia de Apostas com Pouco Dinheiro",
    "description": "Guia completo para apostas pequenas e seguras. Aprenda a transformar R$10 em R$1000 com estratégias de multiplas e gestão de banca.",
    "author": {
      "@type": "Organization",
      "name": "PickStarAI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStarAI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-08-22",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/fazer-fezinha"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Como Fazer a Fezinha 2024 | De R$10 para R$1000 com IA"
        description="Guia completo para fazer a fezinha com segurança. Aprenda estratégias de multiplas, gestão de banca e como transformar R$10 em R$1000 com apostas inteligentes."
        keywords="fazer fezinha, apostas pequenas, multiplas seguras, gestão de banca, apostas R$10, estratégias apostas, como apostar pouco, fezinha online"
        canonical="https://pickstarai.com/fazer-fezinha"
        ogImage="https://pickstarai.com/og-fezinha.jpg"
        structuredData={[articleStructuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Glass layer background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />

        {/* Terminal-style Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/[0.05]">
          <div className="max-w-screen-xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-[0.15em]"
              >
                <ArrowLeft className="w-3 h-3 mr-2" />
                VOLTAR
              </Link>
              <div className="text-[9px] text-white/30 uppercase tracking-[0.2em]">
                ATUALIZADO: {new Date().toLocaleDateString('pt-BR').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="min-h-[60vh] flex items-center justify-center px-8 pt-20 pb-12">
          <div className="text-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 leading-none mb-2">
                De R$10 para R$1000
              </h1>
              <p className="text-xs text-white/50 uppercase tracking-[0.2em] mt-4">
                O guia definitivo para fazer a fezinha com segurança
              </p>
            </motion.div>

            {/* Search Bar */}
            <div className="mt-8 max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar estratégias..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/[0.02] border border-white/[0.05] rounded text-sm text-white/80 placeholder-white/30 focus:outline-none focus:border-white/10 transition-all"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 py-12">

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mb-16 pb-12 border-b border-white/[0.05]">
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">R$10</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">INICIAL</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-emerald-400/80">68%</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">ACERTO</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">3-6</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">MESES</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">50k+</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">USUÁRIOS</div>
            </div>
          </div>

          {/* Strategy Filter */}
          <div className="mb-12">
            <div className="flex gap-2 overflow-x-auto">
              {Object.entries(STRATEGY_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedStrategy(key)}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-all whitespace-nowrap ${
                    selectedStrategy === key
                      ? 'bg-emerald-500/20 text-emerald-400/80 border border-emerald-500/30'
                      : 'bg-white/[0.02] text-white/50 border border-white/[0.05] hover:border-white/10'
                  }`}
                >
                  {label} {key !== 'all' && `(${strategyCount[key] || 0})`}
                </button>
              ))}
            </div>
          </div>

          {/* Step by Step Guide */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">GUIA</span>
              PASSO A PASSO
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {stepGuide.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 border border-emerald-500/20 flex items-center justify-center">
                        <span className="text-emerald-400/80 font-mono text-sm">{index + 1}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-white/80 uppercase tracking-[0.15em] mb-2">
                        {step.title}
                      </h3>
                      <div className="flex items-center gap-3 text-[10px] text-white/40 uppercase tracking-wider">
                        <span>{step.phase}</span>
                        <span>•</span>
                        <span>{step.bankroll}</span>
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-4 border-t border-white/[0.05] pt-4">
                    {step.items.map((item, idx) => (
                      <li key={idx} className="text-sm text-white/50 flex items-start gap-3">
                        <span className="w-1 h-1 bg-emerald-500/50 mt-2" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-4 border-t border-white/[0.05] flex items-center justify-between text-sm">
                    <span className="text-white/40 text-xs uppercase tracking-wider">Retorno esperado:</span>
                    <span className="font-mono text-emerald-400/80">+{step.expectedReturn}%</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Multipla Types */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">ESTRATÉGIAS</span>
              TIPOS DE MULTIPLAS
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMultiplas.map((multipla, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-white/90">{multipla.name}</h3>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider">{multipla.type}</p>
                    </div>
                    <span className="text-2xl font-mono text-emerald-400/80">
                      {multipla.totalOdds}x
                    </span>
                  </div>

                  <p className="text-sm text-white/50 mb-4">{multipla.description}</p>

                  <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-white/[0.05]">
                    <div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Taxa de acerto</div>
                      <div className="text-lg font-mono text-white/80">{multipla.successRate}%</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Investimento</div>
                      <div className="text-lg font-mono text-white/80">{multipla.investment}%</div>
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] text-white/40 uppercase tracking-wider">Melhor para: </span>
                    <span className="text-xs text-white/60">{multipla.bestFor}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Tips */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">DICAS</span>
              REGRAS DE OURO
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {tips.map((tip, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-4 transition-all flex items-start gap-3"
                >
                  <span className="w-1.5 h-1.5 bg-emerald-500/70 mt-2" />
                  <p className="text-sm text-white/60">{tip}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">FAQ</span>
              PERGUNTAS FREQUENTES
            </h2>

            <div className="space-y-0 border border-white/[0.05]">
              {faqs.map((faq, index) => (
                <details key={index} className="group">
                  <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05] last:border-b-0">
                    <span className="text-sm text-white/80 font-medium">{faq.question}</span>
                    <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05] last:border-b-0">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center py-12 border-t border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-4">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-2">COMEÇAR</span>
              SUA FEZINHA COMEÇA AGORA
            </h2>
            <p className="text-sm text-white/50 mb-8 max-w-2xl mx-auto">
              Mais de 50.000 brasileiros já usam nossa IA para fazer a fezinha com segurança e estratégia.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                       text-sm font-medium text-white uppercase tracking-[0.15em]
                       hover:from-emerald-500 hover:to-violet-500 transition-all"
            >
              PRIMEIRA FEZINHA GRÁTIS
              <ChevronRight className="ml-2 w-4 h-4" />
            </button>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-white/[0.05]">
          <SEOFooter language="pt-BR" />
        </div>
      </div>
    </>
  );
};

export default FazerFezinhaPage;
